import {useState} from 'react'
import {Dialog} from '@primer/react/experimental'
import {useArchiveDialog, ARCHIVE_STATUS} from '../contexts/ArchiveDialogContext'
import {useArchiveTask} from '../hooks/use-archive-task'
import {useClosePullRequest} from '../hooks/use-close-pull-request'

/**
 * Confirmation dialog shown only when archiving a task that has an open PR.
 * Offers "Archive only" or "Archive and close PR" options.
 */
export function ArchiveConfirmationDialog() {
  const {dialogState, chatApiUrl, closeArchiveDialog, setArchiveStatus} = useArchiveDialog()
  const {mutateAsync: archiveTaskAsync, isPending: isArchiving} = useArchiveTask({
    repoOwner: dialogState.repoOwner,
    repoName: dialogState.repoName,
    taskId: dialogState.taskId ?? undefined,
    apiUrl: chatApiUrl,
  })
  const {mutateAsync: closePRAsync, isPending: isClosingPR} = useClosePullRequest({
    repoNwo: dialogState.pull?.repository_nwo,
    prNumber: dialogState.pull?.number,
  })
  const [pendingAction, setPendingAction] = useState<'archive' | 'archiveAndClose' | null>(null)
  const isOperationInProgress = isArchiving || isClosingPR

  const handleClose = () => {
    setPendingAction(null)
    closeArchiveDialog()
  }

  const handleArchiveOnly = async () => {
    setPendingAction('archive')
    try {
      await archiveTaskAsync()
    } catch {
      setArchiveStatus(ARCHIVE_STATUS.ERROR)
    }
    handleClose()
  }

  const handleArchiveAndClosePR = async () => {
    setPendingAction('archiveAndClose')
    try {
      await archiveTaskAsync()
      try {
        await closePRAsync()
      } catch {
        setArchiveStatus(ARCHIVE_STATUS.ERROR_PR_CLOSE_FAILED)
      }
    } catch {
      setArchiveStatus(ARCHIVE_STATUS.ERROR)
    }
    handleClose()
  }

  if (!dialogState.isOpen || dialogState.pull?.state !== 'open') {
    return null
  }

  return (
    <Dialog
      title="Archive session"
      width="large"
      onClose={handleClose}
      footerButtons={[
        {
          buttonType: 'normal',
          content: 'Cancel',
          onClick: handleClose,
          disabled: isOperationInProgress,
        },
        {
          buttonType: 'normal',
          content: 'Archive only',
          onClick: handleArchiveOnly,
          disabled: isOperationInProgress,
          loading: pendingAction === 'archive' && isArchiving,
        },
        {
          buttonType: 'normal',
          content: 'Archive and close',
          onClick: handleArchiveAndClosePR,
          disabled: isOperationInProgress,
          loading: pendingAction === 'archiveAndClose' && isOperationInProgress,
        },
      ]}
    >
      This session has an open pull request. Would you like to close it?
    </Dialog>
  )
}
